import {
  CONTENT_DELIVERY_METHOD,
  CONTENT_PROVIDER_D2L,
  CONTENT_PROVIDER_D2L_LABELS,
} from '../../../services/constants';

/**
 * Whether `progressDetails` carries award-backed progress that TranscriptV2 can render in the awards layout.
 * Requires finite numeric `total` and `completed` (percentage-only payloads return false).
 *
 * @param {Object|null|undefined} progressDetails - API `progressDetails` (may be `{}` or omit fields).
 * @returns {boolean}
 */
const hasAwardProgressDetails = (progressDetails) => {
  if (progressDetails == null || typeof progressDetails !== 'object') return false;
  const { total, completed } = progressDetails;
  return Number.isFinite(total) && Number.isFinite(completed);
};

/**
 * True if the program or any of its learning objects has award-backed progress details.
 *
 * @param {Object|null|undefined} program - Transcript program with optional `progressDetails` and `learningObjects`.
 * @returns {boolean}
 */
const programHasAwardProgress = (program) =>
  Boolean(
    hasAwardProgressDetails(program?.progressDetails) ||
      program?.learningObjects?.some((lo) => hasAwardProgressDetails(lo?.progressDetails))
  );

/**
 * True if any program in the content section has award-backed progress (used for section-level `isProgramAwardable`).
 *
 * @param {Object|null|undefined} content - Transcript `contents[]` item with `programs`.
 * @returns {boolean}
 */
export const contentSectionHasAwardProgress = (content) =>
  Array.isArray(content?.programs) && content.programs.some(programHasAwardProgress);

/**
 * @param {Object|null|undefined} provider - Content provider from transcript payload.
 * @returns {boolean}
 */
export const isEssentialEd = (provider) => provider?.tag === CONTENT_DELIVERY_METHOD.ESSENTIAL_ED;

/**
 * @param {Object|null|undefined} provider - Content provider from transcript payload.
 * @returns {boolean}
 */
export const isInterplay = (provider) =>
  provider?.name === CONTENT_PROVIDER_D2L_LABELS[CONTENT_PROVIDER_D2L.INTERPLAY];

/**
 * Maps API learning-object `timeSpentMinutes` to the shape TranscriptV2 expects (`totalTimeSpentMinutes`).
 *
 * @param {Object} learningObject - Must be destructurable; typically includes `timeSpentMinutes` and other API fields.
 * @returns {Object} Spread of remaining fields plus `totalTimeSpentMinutes` (from `timeSpentMinutes`).
 */
const transformLearningObject = ({ timeSpentMinutes, ...restLo }) => ({
  ...restLo,
  totalTimeSpentMinutes: timeSpentMinutes,
});

/**
 * Maps API program `totalTimeSpentMinutes` to `timeSpentMinutes` and transforms nested learning objects.
 *
 * @param {Object} program - API program with `totalTimeSpentMinutes`, `learningObjects`, and other fields.
 * @returns {Object} Transformed program for TranscriptV2.
 */
export const transformProgram = (program) => {
  const { totalTimeSpentMinutes, learningObjects = [], ...restProgram } = program;
  return {
    ...restProgram,
    timeSpentMinutes: totalTimeSpentMinutes,
    learningObjects: learningObjects.map(transformLearningObject),
  };
};
